import styled from "@emotion/styled";
import React, { HTMLAttributes, useMemo } from "react";
import { Column, Row } from "@components/Flex";
import { ReactComponent as Logo } from "@assets/icons/logo.svg";
import { ReactComponent as ToggleMenuIcon } from "@assets/icons/toggleMenu.svg";
import { ReactComponent as MapIcon } from "@assets/icons/map.svg";
import { ReactComponent as RouterIcon } from "@assets/icons/router.svg";
import { ReactComponent as TrackIcon } from "@assets/icons/track.svg";
import { ReactComponent as TreeIcon } from "@assets/icons/tree.svg";
import { ReactComponent as AgentIcon } from "@assets/icons/agent.svg";
import { ReactComponent as AnalyticsIcon } from "@assets/icons/analytics.svg";
import { ReactComponent as ReportIcon } from "@assets/icons/report.svg";
import { ReactComponent as ProfileIcon } from "@assets/icons/profile.svg";
import { ReactComponent as SettingsIcon } from "@assets/icons/settings.svg";
import { ROUTES } from "@stores/AccountStore";
import LeftBarMenuItem from "./LeftBarMenuItem";
import useWindowSize from "@src/hooks/useWindowSize";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import useHover from "@src/hooks/useHover";
import useIsMobile from "@src/hooks/useIsMobile";
import { useLang } from "@src/hooks/useLang";
import AccountSwitcher from "./AccountSwitcher";
import Seasons from "@components/Layout/Seasons";
import Farms from "@components/Layout/Farms/Farms";
import { canOpenRoute } from "@src/utils/navigation";

interface IProps extends HTMLAttributes<HTMLDivElement> { }

const calcMargin = (width: number) => {
  switch (width) {
    case 272:
      return 24;
    case 186:
      return 20;
    case 52:
      return 32;
    default:
      return 24;
  }
};

const Root = styled.div<{ width: number; expanded: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: #f8f8f8;
  min-width: ${({ width, expanded }) => (expanded ? width : 52)}px;
  max-width: ${({ width, expanded }) => (expanded ? width : 52)}px;
  margin-right: ${({ width }) => width};
  transition: 0.4s;
  margin-right: ${({ width, expanded }) => calcMargin(expanded ? width : 52)}px;
  z-index: 1;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  padding: 0 16px;
  box-sizing: border-box;
  white-space: nowrap;
  text-overflow: ellipsis;
  border-bottom: 1px solid rgba(137, 144, 147, 0.3);
  /* min-height (not a fixed height) so the header grows to fit the Season +
     Farm selectors when expanded instead of clipping the logo off the top;
     collapsed (no Farm selector) it stays at the original 94px. padding-top
     preserves the logo's top breathing room now that the height isn't fixed. */
  min-height: 94px;
  padding-top: 14px;
`;

const Body = styled.div`
  flex-direction: column;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 16px 0;
`;

const LeftBar: React.FC<IProps> = ({ ...rest }) => {
  const isMobile = useIsMobile();
  const { settingsStore, accountStore } = useStores();
  const lang = useLang();
  const size = useWindowSize();
  const width = size.width ?? document.documentElement.clientWidth;
  const expanded = settingsStore.leftBarExpanded && (width ?? 1280) > 1140;
  const [hoverRef] = useHover<HTMLDivElement>();
  const expand = () => settingsStore.setLeftBarExpanded(true);
  const close = () => settingsStore.setLeftBarExpanded(false);
  const currentWidth = hoverRef?.current?.offsetWidth;
  const rootTransition = width != null && currentWidth != null ? "0.4s" : "0";

  const reportsMenuTitle = (lang.menu as any).reportsMenuOption ?? "Reports";

  // Team members only see the devices/tracks/analytics area when their group
  // allows it, and never the AI agent (owner-only in v1). The agent is also a
  // paid feature — every chat turn costs LLM tokens.
  const canDevices = accountStore.canAccessDevices;
  // A role can hide fields & yield maps entirely (the backend 403s them), so
  // their screens — Fields and the field-based Reports — go with it.
  const canFields = accountStore.canViewFields;
  const isMember = accountStore.isMember;
  const subscriptionPaid = accountStore.subscriptionPaid;

  const navigationLinks = useMemo(() => {
    const links = [
      {
        title: lang.menu.fieldMenuOption,
        icon: <MapIcon />,
        route: ROUTES.FIELDS,
      },
      {
        title: lang.menu.devicesMenuOption,
        icon: <RouterIcon />,
        route: ROUTES.DEVICES,
      },
      {
        title: lang.menu.cropsMenuOption,
        icon: <TreeIcon />,
        route: ROUTES.CROPS,
      },
      {
        title: lang.menu.tracksMenuOption,
        icon: <TrackIcon />,
        route: ROUTES.TRACKS,
      },
      {
        title: lang.menu.analyticsMenuOption,
        icon: <AnalyticsIcon />,
        route: ROUTES.ANALYTICS,
      },
      {
        title: reportsMenuTitle,
        icon: <ReportIcon />,
        route: ROUTES.REPORTS,
      },
      {
        title: lang.menu.agentMenuOption,
        icon: <AgentIcon />,
        route: ROUTES.AGENT,
      },
    ];
    // One rule set with the mobile bar and the route guard (utils/navigation).
    const caps = {
      canViewFields: canFields,
      canAccessDevices: canDevices,
      isMember,
      subscriptionPaid,
    };
    return links.filter((l) => canOpenRoute(caps, l.route));
  }, [
    lang.menu.cropsMenuOption,
    lang.menu.devicesMenuOption,
    lang.menu.fieldMenuOption,
    lang.menu.tracksMenuOption,
    lang.menu.agentMenuOption,
    lang.menu.analyticsMenuOption,
    reportsMenuTitle,
    canDevices,
    canFields,
    isMember,
    subscriptionPaid,
  ]);

  if (width == null) return null;
  if (isMobile) return null;

  const menu_width = (width ?? 1281) > 1280 ? 200 : 150;

  return (
    <Root
      style={{ transition: rootTransition }}
      width={menu_width}
      expanded={expanded}
      ref={hoverRef}
      {...rest}
    >
      <Header>
        <Row
          alignItems="center"
          justifyContent="space-between"
          style={{ height: 36 }}
        >
          <Logo style={{ minWidth: 24, width: 24 }} />
          {expanded && (
            <ToggleMenuIcon style={{ cursor: "pointer" }} onClick={close} />
          )}
        </Row>
        {expanded ? (
          <>
            <Seasons
              expanded={expanded}
              menuWidth={menu_width}
            />
            <Farms
              expanded={expanded}
              menuWidth={menu_width}
            />
          </>
        ) : (
          <ToggleMenuIcon
            onClick={expand}
            style={{
              transform: "rotate(180deg)",
              height: 38,
              cursor: "pointer",
              opacity: (width ?? 1280) > 1140 ? 1 : 0,
            }}
          />
        )}
      </Header>
      <Body>
        <Column crossAxisSize="max">
          {navigationLinks.map((link) => (
            <LeftBarMenuItem expanded={expanded} {...link} key={link.route} />
          ))}
        </Column>
        <Column crossAxisSize="max">
          <LeftBarMenuItem
            expanded={expanded}
            title={lang.menu.settingsMenuOption}
            icon={<SettingsIcon />}
            route={ROUTES.SETTINGS}
            type="secondary"
          />
          <LeftBarMenuItem
            expanded={expanded}
            title={lang.menu.profileMenuOption}
            icon={<ProfileIcon />}
            route={ROUTES.PROFILE}
            type="secondary"
          />
          <AccountSwitcher
            expanded={expanded}
            menuWidth={menu_width}
          />
        </Column>
      </Body>
    </Root>
  );
};
export default observer(LeftBar);
